import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Avatar, Brush } from '@producer/ui-kit';
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';

import { CommentCheckbox } from './CommentCheckbox';
import { Tooltip } from '../tooltip/Tooltip';
import { formatDateAndTimeShort } from '../../core/services/date.service';
import { secondsToHms } from '../../lib/utils/Convertor';
import { catchError } from '../../core/catch-error';
import { CommentForm } from './comment-form';

import type { Comment } from '../../app/entities';

import styles from './CommentItem.module.scss';
interface CommentItemProps {
  comment: Comment;
  isReply?: boolean;
  onClick?: (comment: Comment) => void;

  /**
   * Override default reply behavior
   */
  onReply?: (reply: { text: string; parentId: string }) => void;
}

export const CommentItem: React.FC<CommentItemProps> = observer(
  ({ comment, onClick, isReply, onReply }) => {
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [isReplying, setIsReplying] = useState<boolean>(false);

    const handleClick = () => {
      onClick && onClick(comment);
    };

    const startUpdate = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsUpdating((prev) => !prev);
    };

    const stopUpdate = (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }

      setIsUpdating(false);
    };

    const handleDelete = async (e: React.MouseEvent) => {
      e.stopPropagation();
      await comment.delete();
    };

    const handleUpdate = async (text: string) => {
      await comment.update({ text });
      stopUpdate();
    };

    const startReply = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsReplying(true);
    };

    const stopReply = () => {
      setIsReplying(false);
    };

    const handleReply = async (text: string) => {
      try {
        onReply ? onReply({ text, parentId: comment._id }) : await comment.reply(text);
      } catch (e) {
        catchError(e);
      }
      stopReply();
    };

    const handleValidateComment = async () => {
      await comment.complete(!comment.completed);
    };

    return (
      <div className={styles['container']} id={comment._id} onClick={handleClick}>
        <div className={styles['header']}>
          <div className={styles['left']}>
            <Avatar
              src={comment.author?.avatar}
              initials={comment.authorInitials}
              size="small"
              theme="dark"
            />

            {comment.authorName && <div className={styles['author']}>{comment.authorName}</div>}

            {comment.createdAt && (
              <div className={styles['time-stamp']}>
                {comment.createdAt ? formatDateAndTimeShort(new Date(comment.createdAt)) : '-'}
              </div>
            )}
          </div>

          {isReply && (
            <div className={styles['actions']}>
              {comment.isAuthor && (
                <>
                  <div className={styles['action-container']} onClick={startUpdate}>
                    <Pencil1Icon />
                  </div>

                  <div className={styles['action-container']} onClick={handleDelete}>
                    <TrashIcon />
                  </div>
                </>
              )}
            </div>
          )}

          {!isReply && (
            <Tooltip text="Mark as completed" position="left">
              <CommentCheckbox checked={comment.completed} onClick={handleValidateComment} />
            </Tooltip>
          )}
        </div>

        {isUpdating ? (
          <CommentForm
            formId={`update-comment-form-${comment._id}`}
            onSubmit={handleUpdate}
            defaultValue={comment.text}
            onCancel={stopUpdate}
          />
        ) : (
          <>
            <div className={styles['comment-container']}>
              {!isReply && (comment.annotations.length > 0 || comment.timestamp) && (
                <div className={styles['time-code-container']}>
                  {comment.annotations &&
                    comment.annotations.length > 0 &&
                    comment.annotations[0].path && (
                      <div className={styles['icon-container']}>
                        <Brush />
                      </div>
                    )}

                  {comment.timestamp && (
                    <div className={styles['time-code']}>{secondsToHms(comment.timestamp)}</div>
                  )}
                </div>
              )}

              <div className={styles['text']} dangerouslySetInnerHTML={{ __html: comment.text }} />
            </div>

            {!isReply && (
              <>
                <div className={styles['footer']}>
                  <span onClick={startReply}>Reply</span>

                  {comment.isAuthor && (
                    <div className={styles['actions']}>
                      <div className={styles['action-container']} onClick={startUpdate}>
                        <Pencil1Icon />
                      </div>

                      <div className={styles['action-container']} onClick={() => comment.delete()}>
                        <TrashIcon />
                      </div>
                    </div>
                  )}
                </div>

                {isReplying && (
                  <CommentForm
                    formId={`reply-to-comment-form-${comment._id}`}
                    onSubmit={handleReply}
                    onCancel={stopReply}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  },
);
